import { useState, useEffect } from 'react';

import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { fDate } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';

import { fetchAllLoanAgreements } from 'src/redux/slices/loan-agreement';

import Label from 'src/components/label';
import Scrollbar from 'src/components/scrollbar';
import { TableSkeleton } from 'src/components/table';

import { LoanAgreementStatus, ILoanAgreementListItem } from 'src/types/loan-agreement';

import { LOAN_STATUS_LABEL } from './utils';

type Props = {
  employeeId: string;
};

/**
 * Every loan agreement (any status) for one employee, shown on the
 * employee details page.
 */
export default function EmployeeLoans({ employeeId }: Props) {
  const [rows, setRows] = useState<ILoanAgreementListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAllLoanAgreements({ employee: employeeId, sortBy: 'loanPaidDate', sort: 'desc' })
      .then((result) => {
        if (!cancelled) setRows(result);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message ?? 'Failed to load loans');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [employeeId]);

  return (
    <TableContainer sx={{ mt: 2 }}>
      <Scrollbar>
        <Table size="small" sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell>Loan Paid</TableCell>
              <TableCell align="right">Capital</TableCell>
              <TableCell>Settlement Date</TableCell>
              <TableCell sx={{ minWidth: 180 }}>Paid</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && [...Array(2)].map((_, index) => <TableSkeleton key={index} />)}

            {!loading && (error || rows.length === 0) && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color={error ? 'error' : 'text.secondary'}>
                    {error ?? 'No loans for this employee.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fDate(row.loanPaidDate)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {fCurrencyRupees(row.capitalInCents)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fDate(row.settlementDate)}</TableCell>
                  <TableCell>
                    <Tooltip title={`${fCurrencyRupees(row.remainingInCents)} remaining`} arrow>
                      <LinearProgress
                        variant="determinate"
                        value={row.progressPercent}
                        color={row.status === LoanAgreementStatus.SETTLED ? 'success' : 'primary'}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Label variant="soft" color={LOAN_STATUS_LABEL[row.status].color}>
                      {LOAN_STATUS_LABEL[row.status].label}
                    </Label>
                    {row.overdueAmountInCents > 0 && row.status === LoanAgreementStatus.ACTIVE && (
                      <Label variant="soft" color="warning" sx={{ ml: 1 }}>
                        Overdue
                      </Label>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Link component={RouterLink} href={paths.dashboard.loans.view(row._id)}>
                      <Button size="small" variant="soft" color="primary">
                        View
                      </Button>
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Scrollbar>
    </TableContainer>
  );
}
